/**
 * Habit date utilities — frequency-aware date checks.
 * Shared across grid, checkbox, stats, pie chart.
 *
 * IMPORTANT: weekly_days uses ISO day (1=Mon … 7=Sun) from getISODay.
 */

import { getISODay, getDaysInMonth } from 'date-fns'
import type { MonthlyHabitItem } from '@turnifly/shared/types'

/** Check if a habit applies on a specific date (YYYY-MM-DD) */
export function habitAppliesToDate(
  habit: Pick<MonthlyHabitItem, 'frequency' | 'weekly_days' | 'custom_interval' | 'start_date' | 'end_date'>,
  dateStr: string,
): boolean {
  if (habit.start_date && dateStr < habit.start_date) return false
  if (habit.end_date && dateStr > habit.end_date) return false

  const freq = habit.frequency
  if (freq === 'daily' || !freq) return true

  if (freq === 'weekly_days') {
    if (!habit.weekly_days || habit.weekly_days.length === 0) return false
    const isoDay = getISODay(new Date(dateStr))
    return habit.weekly_days.includes(isoDay)
  }

  if (freq === 'custom_interval') {
    if (!habit.custom_interval || !habit.start_date) return false
    const startMs = new Date(habit.start_date).getTime()
    const checkMs = new Date(dateStr).getTime()
    const diffDays = Math.round((checkMs - startMs) / 86_400_000)
    return diffDays >= 0 && diffDays % habit.custom_interval === 0
  }

  return true // unknown frequency — treat as daily
}

/** Count how many days in a month a habit applies to */
export function countApplicableDays(
  habit: Pick<MonthlyHabitItem, 'frequency' | 'weekly_days' | 'custom_interval' | 'start_date' | 'end_date'>,
  year: number,
  month: number,
): number {
  const totalDays = getDaysInMonth(new Date(year, month - 1))
  const mm = String(month).padStart(2, '0')
  let count = 0

  for (let d = 1; d <= totalDays; d++) {
    const ds = `${year}-${mm}-${String(d).padStart(2, '0')}`
    if (habitAppliesToDate(habit, ds)) count++
  }

  return count
}

/** Format day number to YYYY-MM-DD */
export function formatDateStr(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** Today as YYYY-MM-DD in device local timezone (UTC+5 for UZ users) */
export function getLocalTodayStr(): string {
  const now = new Date()
  return formatDateStr(now.getFullYear(), now.getMonth() + 1, now.getDate())
}

// ── Day stats (shared by grid + stats-row) ──────────────────

export interface DayStats { completed: number; total: number; rate: number }

/** Build a Map<dateStr, Set<habitId>> from completed logs for O(1) lookup */
export function buildLogIndex(habits: MonthlyHabitItem[]): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>()
  for (const h of habits) {
    for (const log of h.logs) {
      if (!log.completed) continue
      let set = index.get(log.log_date)
      if (!set) { set = new Set(); index.set(log.log_date, set) }
      set.add(h.id)
    }
  }
  return index
}

/** Calculate completed/total/rate for each day in the given days array */
export function calcAllDayStats(
  habits: MonthlyHabitItem[],
  days: number[],
  year: number,
  month: number,
  logIndex: Map<string, Set<string>>,
): DayStats[] {
  const mm = String(month).padStart(2, '0')
  return days.map((day) => {
    const ds = `${year}-${mm}-${String(day).padStart(2, '0')}`
    const completedSet = logIndex.get(ds)
    let applicable = 0
    let completed = 0
    for (const h of habits) {
      if (habitAppliesToDate(h, ds)) { applicable++; if (completedSet?.has(h.id)) completed++ }
    }
    return { completed, total: applicable, rate: applicable > 0 ? Math.round((completed / applicable) * 100) : 0 }
  })
}

/** Check if a date can be toggled (today or yesterday before 3AM) */
export function isDateToggleable(dateStr: string, todayDateStr: string): boolean {
  const now = new Date()
  const cellDate = new Date(dateStr + 'T00:00:00')
  const todayDate = new Date(todayDateStr + 'T00:00:00')

  // Today — always toggleable
  if (dateStr === todayDateStr) return true

  // Yesterday — toggleable until 3AM
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const yStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`
  if (dateStr === yStr && now.getHours() < 3) return true

  // Future — not toggleable
  if (cellDate > todayDate) return false

  // Past — not toggleable
  return false
}
